import { useEffect, useState } from "react";
import * as http from "../../../utils/http";

export type DocumentsRequired = "photos_and_id" | "photos" | "id" | "none";

export type ThankyouDocuments = {
    /** False until the check settles; the page shows the No card layout meanwhile. */
    settled: boolean;
    /** True once a check came back with nothing outstanding, while the retries for the webhook race still run. */
    answered: boolean;
    needsPhoto: boolean;
    needsId: boolean;
};

// The payment webhook moves the order to "waiting for customer actions"; the thank-you page can load
// first, so a not-yet-waiting order is asked again a few times before it counts as "nothing outstanding".
const RETRY_DELAY_MS = 2000;
const MAX_RETRIES = 3;

export const getDocumentsRequired = ({ needsPhoto, needsId }: Pick<ThankyouDocuments, "needsPhoto" | "needsId">): DocumentsRequired =>
    needsPhoto && needsId ? "photos_and_id" : needsPhoto ? "photos" : needsId ? "id" : "none";

/**
 * HL-12639: which verification tasks are outstanding, from the check My Account uses
 * (GET pending-actions). A failed check shows both tasks (HL-11038 AC-6: show the task).
 */
export default function useThankyouDocuments(country: string | undefined, hashID: string | undefined, enabled: boolean): ThankyouDocuments {
    const [documents, setDocuments] = useState<ThankyouDocuments>({ settled: false, answered: false, needsPhoto: false, needsId: false });

    useEffect(() => {
        if (!enabled || !country || !hashID) return;
        let cancelled = false;
        let retryTimer: ReturnType<typeof setTimeout> | undefined;

        const check = (attempt: number) => {
            http
                .getFromServer(`${country}/account/orders/${hashID}/pending-actions`, [http.MIDDLEWARES.CUSTOMER_ID])
                .then((res: any) => {
                    if (cancelled) return;
                    const needsPhoto = res.data?.needsPhotoUpload === true;
                    const needsId = res.data?.needsIdVerification === true;
                    if (!needsPhoto && !needsId && res.data?.isWaitingActions !== true && attempt < MAX_RETRIES) {
                        setDocuments((current) => (current.answered ? current : { ...current, answered: true }));
                        retryTimer = setTimeout(() => check(attempt + 1), RETRY_DELAY_MS);
                        return;
                    }
                    setDocuments({ settled: true, answered: true, needsPhoto, needsId });
                })
                .catch(() => {
                    if (!cancelled) setDocuments({ settled: true, answered: true, needsPhoto: true, needsId: true });
                });
        };
        check(0);

        return () => {
            cancelled = true;
            clearTimeout(retryTimer);
        };
    }, [country, hashID, enabled]);

    return documents;
}
